import { store } from 'store/store';
import { ConnectionQuality } from 'utils/webrtc/ConnectionQuality';
import type { Client } from 'utils/webrtc/Client';
import {
  statsTickPublished,
  selectConnectionQualityLevel,
  selectOrchestratorInputs,
} from 'features/system-stats/systemStatsSlice';
import { selectLocalBroadcasting } from 'features/streaming/streamingSlice';
import { extractReceivingFields } from 'utils/webrtc/stats/extract-receiving-fields';
import { extractStatsGraph } from 'utils/webrtc/stats/extract-track-fields';
import { aggregateNetworkTotals, aggregateTrackScores } from 'utils/webrtc/stats/aggregators';
import { extractPublishingLayers, legacyPublisherMOS } from 'utils/webrtc/stats/legacy-publisher';
import { logPressure, logTick } from 'utils/webrtc/stats/stats-logger';
import { scoreGraph } from 'utils/webrtc/stats/score-graph';
import { WarmupTracker } from 'utils/webrtc/stats/WarmupTracker';
import { BucketTransitionEmitter } from 'utils/webrtc/stats/BucketTransitionEmitter';
import { collectPCs } from 'utils/webrtc/stats/pc-discovery';
import { FeedId } from 'features/streaming/types';
import { StatsGraph, TrackScore } from 'utils/webrtc/stats/types';
import { getStatsTrace } from 'utils/webrtc/stats/install-rtcstats';

const DEFAULT_INTERVAL_MS = 2000; // 0.5 Hz
const PER_PC_TIMEOUT_MS = 500;
const PRESSURE_THRESHOLD_MS = 250;

const timeout = (ms: number): Promise<null> =>
  new Promise((resolve) => {
    setTimeout(() => resolve(null), ms);
  });

export class StatsOrchestrator {
  private readonly rtcClient: Client;

  private readonly intervalMs: number;

  private timerId?: number;

  private tickNumber = 0;

  private previousGraphs = new Map<string, StatsGraph>();

  private readonly warmup = new WarmupTracker();

  private readonly bucketEmitter = new BucketTransitionEmitter();

  connectionQuality?: ConnectionQuality;

  constructor(opts: { rtcClient: Client; intervalMs?: number }) {
    this.rtcClient = opts.rtcClient;
    this.intervalMs = opts.intervalMs ?? DEFAULT_INTERVAL_MS;
  }

  start(): void {
    if (this.timerId !== undefined) {
      return;
    }
    this.connectionQuality = new ConnectionQuality();
    this.connectionQuality.startHysteresisLoop();
    // First tick fires immediately to avoid a dead window before the first interval.
    this.tick().catch(() => {
      /* swallow — pressure logged inside tick */
    });
    this.timerId = window.setInterval(() => {
      this.tick().catch(() => {
        /* swallow — pressure logged inside tick */
      });
    }, this.intervalMs) as unknown as number;
  }

  stop(): void {
    if (this.timerId !== undefined) {
      window.clearInterval(this.timerId);
      this.timerId = undefined;
    }
    this.connectionQuality?.stopHysteresisLoop();
    this.connectionQuality = undefined;
    this.previousGraphs.clear();
    this.warmup.clear();
    this.bucketEmitter.reset();
  }

  // PCs are torn down on breakout — clear room-scoped state without halting polling.
  resetForRoomChange(): void {
    this.previousGraphs.clear();
    this.warmup.clear();
    this.bucketEmitter.reset();
    // Explicit room-boundary reset; feedGetStats auto-resets on pcId change.
    getStatsTrace()?.reset();
    this.tickNumber = 0;
    // Only recycle ConnectionQuality while ticks are running — its hysteresis
    // interval would leak otherwise.
    if (this.timerId === undefined) {
      return;
    }
    this.connectionQuality?.stopHysteresisLoop();
    this.connectionQuality = new ConnectionQuality();
    this.connectionQuality.startHysteresisLoop();
  }

  private async tick(): Promise<void> {
    this.tickNumber += 1;
    const tickStartedAt = performance.now();

    const allPCs = collectPCs(this.rtcClient); // ≤3 entries: publisher + screenshare + receiver

    // Read inputs every tick — orchestrator self-corrects if localUserId appears late.
    const state = store.getState();
    const {
      localUserId,
      localFeedId,
      localScreenshareFeedId,
      sendingAudioOn,
      sendingScreenshareAudioOn,
      broadcastingByUserId,
      userIdByFeedId,
      connectionQualityV2Enabled,
    } = selectOrchestratorInputs(state);
    const isRemoteAudioOn = (feedId: FeedId | null): boolean => {
      if (feedId === null) {
        return true;
      }
      const userId = userIdByFeedId[feedId];
      if (userId === undefined) {
        return true;
      }
      // Undefined audio state (pre-signalling) is treated as broadcasting to
      // avoid suppressing scores during the brief join window.
      return broadcastingByUserId[userId]?.audio !== false;
    };

    // += is safe: feedGetStats is synchronous despite the outer Promise.allSettled.
    let rtcStatsCompressionMs = 0;
    const results = await Promise.allSettled(
      allPCs.map(async ({ pcKey, pc, context }) => {
        const report = await Promise.race([pc.getStats(), timeout(PER_PC_TIMEOUT_MS)]);
        if (!report) {
          return null;
        }
        // __rtcStatsId is tagged by the rtcstats wrapper; falls back to pcKey if the wrapper wasn't installed.
        // eslint-disable-next-line no-underscore-dangle
        const pcId = (pc as { __rtcStatsId?: string }).__rtcStatsId ?? pcKey;
        rtcStatsCompressionMs += getStatsTrace()?.feedGetStats(pcKey, pcId, report) ?? 0;
        const graph = extractStatsGraph(report, this.previousGraphs.get(pcKey) ?? null, context);
        this.previousGraphs.set(pcKey, graph);
        return { pcKey, graph, context };
      })
    );

    // Evict previous-graph entries for PCs no longer in the session
    // (e.g., publisher feed went away mid-session)
    const livePCKeys = new Set(allPCs.map((p) => p.pcKey));
    for (const key of this.previousGraphs.keys()) {
      if (!livePCKeys.has(key)) {
        this.previousGraphs.delete(key);
      }
    }

    const allScores: TrackScore[] = [];
    const liveGraphs: StatsGraph[] = [];
    const observedTrackKeysThisTick = new Set<string>();
    const { feedIdByMid, availableScreenshares } = this.rtcClient.receivingFeed;
    const isScreenshareFeed = (feedId: FeedId | null): boolean =>
      feedId !== null && feedId in availableScreenshares;
    results.forEach((res) => {
      if (res.status !== 'fulfilled' || !res.value) {
        return;
      }
      const { graph, pcKey } = res.value;
      liveGraphs.push(graph);
      const sendingFeedId = pcKey === 'ss-pub' ? localScreenshareFeedId : localFeedId;
      allScores.push(
        ...scoreGraph(
          graph,
          {
            // ss-pub audio answers to the screenshare's own tab-audio state, not the
            // camera mic; V2-gated so flag-off stays byte-identical to legacy.
            sendingAudioOn:
              pcKey === 'ss-pub' && connectionQualityV2Enabled
                ? sendingScreenshareAudioOn
                : sendingAudioOn,
            isRemoteAudioOn,
            videoFrameHealthEnabled: connectionQualityV2Enabled,
            isScreenshareFeed,
            pcStabilityEnabled: connectionQualityV2Enabled,
          },
          this.warmup,
          feedIdByMid,
          sendingFeedId,
          observedTrackKeysThisTick
        )
      );
    });

    // Evict per-track counters unseen this tick — a resubscribe should replay
    // warmup since deltas are bogus again after a gap.
    this.warmup.evictUnseen(observedTrackKeysThisTick);

    const pubGraph = this.previousGraphs.get('pub');
    const outboundVideo = pubGraph?.outboundVideo ?? new Map();
    const legacyMOS = legacyPublisherMOS(outboundVideo);
    const publishingLayers = extractPublishingLayers(outboundVideo);

    const aggregate = aggregateTrackScores(allScores);
    const networkTotals = aggregateNetworkTotals(liveGraphs);
    const receiverGraph = this.previousGraphs.get('rcv') ?? null;
    const receivingFields = extractReceivingFields(receiverGraph, feedIdByMid);
    store.dispatch(
      statsTickPublished({
        trackScores: allScores,
        aggregate,
        networkTotals,
        legacyPublishingMOS: legacyMOS,
        publishingLayers,
        receivingFields,
      })
    );

    getStatsTrace()?.traceScores({ trackScores: allScores, aggregate });

    const indicatorMOS = connectionQualityV2Enabled
      ? aggregate.aggregateMOSMedianReceiving
      : legacyMOS;
    if (this.connectionQuality) {
      if (indicatorMOS !== null) {
        // worstPublishing drives the toast gate independently from the indicator
        // so listeners (worstPublishing=null) never trigger it; V2-off uses legacyMOS for both.
        this.connectionQuality.recordSample({
          indicatorMOS,
          publishingMOS: connectionQualityV2Enabled ? aggregate.worstPublishing : legacyMOS,
        });
      } else {
        this.connectionQuality.recordEmptyTick();
      }
    }

    // Uses the hysteresis-derived level from before this tick, not the raw MOS.
    this.bucketEmitter.observe(aggregate, allScores, selectConnectionQualityLevel(state));

    const tickDurationMs = performance.now() - tickStartedAt;
    logTick(
      {
        userId: localUserId,
        tickNumber: this.tickNumber,
        tickDurationMs,
        rtcStatsCompressionMs,
        legacyMOS,
        aggregate,
        trackScores: allScores,
      },
      selectLocalBroadcasting(state)
    );

    if (tickDurationMs > PRESSURE_THRESHOLD_MS) {
      logPressure(tickDurationMs, {
        pcCount: allPCs.length,
        sampledTracks: allScores.length,
      });
    }
  }
}
